import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listApiKeys, createApiKey, revokeApiKey, ApiKey } from '../lib/api';
import { Plus, Trash2, Key, Copy, Check, Loader2 } from 'lucide-react';

export default function ApiKeys() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyTier, setNewKeyTier] = useState<'public' | 'internal' | 'admin'>('admin');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: listApiKeys,
  });

  const createMutation = useMutation({
    mutationFn: () => createApiKey(newKeyName, newKeyTier),
    onSuccess: (data) => {
      setCreatedKey(data.key_value);
      setNewKeyName('');
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: revokeApiKey,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  const handleCopy = (key: string) => {
    navigator.clipboard.writeText(key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-100">API Keys</h1>
        <button
          onClick={() => { setShowCreate(true); setCreatedKey(null); }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-md transition-colors"
        >
          <Plus className="h-4 w-4" /> Generate New Key
        </button>
      </div>

      {showCreate && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
          {!createdKey ? (
            <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Key Name</label>
                <input
                  required
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., Production Admin Key"
                  className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Access Tier</label>
                <select
                  value={newKeyTier}
                  onChange={(e) => setNewKeyTier(e.target.value as any)}
                  className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="admin">Admin (Full Access)</option>
                  <option value="internal">Internal (Standard Access)</option>
                  <option value="public">Public (Read/Limited Access)</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || !newKeyName.trim()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-md transition-colors disabled:opacity-50"
                >
                  {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Key className="h-4 w-4" />}
                  Create Key
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-md p-4 flex items-start gap-3">
                <Key className="h-5 w-5 text-amber-400 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-amber-400">Save this key immediately</h3>
                  <p className="text-sm text-amber-200/70 mt-1">
                    For security reasons, this key will only be shown once. If you lose it, you will need to generate a new one.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={createdKey}
                  className="flex-1 bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm font-mono text-emerald-400 focus:outline-none"
                />
                <button
                  onClick={() => handleCopy(createdKey)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-md transition-colors flex items-center gap-2"
                >
                  {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <button
                onClick={() => setShowCreate(false)}
                className="w-full py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-md transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>
      )}

      <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-slate-400 uppercase bg-slate-800/50">
            <tr>
              <th className="px-6 py-3">Name</th>
              <th className="px-6 py-3">Tier</th>
              <th className="px-6 py-3">Created</th>
              <th className="px-6 py-3">Last Used</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {isLoading ? (
              <tr><td colSpan={5} className="px-6 py-8 text-center text-slate-500">Loading keys...</td></tr>
            ) : data?.items.length === 0 ? (
              <tr><td colSpan={5} className="px-6 py-8 text-center text-slate-500">No API keys found.</td></tr>
            ) : (
              data?.items.map((key: ApiKey) => (
                <tr key={key.id} className="hover:bg-slate-800/30 transition-colors">
                  <td className="px-6 py-4 font-medium text-slate-200">{key.name}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                      key.tier === 'admin' ? 'bg-red-500/10 text-red-400' :
                      key.tier === 'internal' ? 'bg-blue-500/10 text-blue-400' :
                      'bg-slate-700 text-slate-300'
                    }`}>
                      {key.tier}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-400 text-xs">
                    {new Date(key.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-slate-400 text-xs">
                    {key.last_used_at ? new Date(key.last_used_at).toLocaleString() : 'Never'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {key.revoked_at ? (
                      <span className="text-xs text-slate-600 italic">Revoked</span>
                    ) : (
                      <button
                        onClick={() => {
                          if (confirm(`Are you sure you want to revoke "${key.name}"? This cannot be undone.`)) {
                            revokeMutation.mutate(key.id);
                          }
                        }}
                        disabled={revokeMutation.isPending}
                        className="text-slate-400 hover:text-red-400 transition-colors disabled:opacity-50"
                        title="Revoke Key"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
